import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { api } from "@/lib/apiClient";

interface SupplierOrderLine {
  id: string;
  article_name: string;
  quantity: string;
}

interface SupplierOrder {
  id: string;
  supplier_id: string;
  delivery_date: string;
  status: string;
  lines: SupplierOrderLine[];
}

export interface SupplierOpenOrdersPanelProps {
  supplierId: string;
  canSend: boolean;
}

export function SupplierOpenOrdersPanel({ supplierId, canSend }: SupplierOpenOrdersPanelProps) {
  const [orders, setOrders] = useState<SupplierOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendingDate, setSendingDate] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get<SupplierOrder[]>(
        `/api/v1/suppliers/${supplierId}/orders?status=open`,
      )
      .then((rows) => setOrders(rows.filter((row) => row.lines.length > 0)))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [supplierId]);

  useEffect(() => {
    load();
  }, [load]);

  async function sendOrder(deliveryDate: string) {
    setSendingDate(deliveryDate);
    setError(null);
    try {
      await api.post(`/api/v1/suppliers/${supplierId}/orders/${deliveryDate}/send`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Versand fehlgeschlagen");
    } finally {
      setSendingDate(null);
    }
  }

  if (loading) {
    return <p className="text-sm text-label-secondary">Offene Bestellungen werden geladen…</p>;
  }

  if (orders.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3 rounded-lg border border-separator bg-fill-tertiary p-4">
      <h3 className="text-sm font-medium">Offene Bestellungen</h3>
      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      <ul className="space-y-3">
        {orders.map((order) => (
          <li key={order.id} className="rounded-lg border border-separator bg-background p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-medium">
                Lieferung am{" "}
                {new Date(`${order.delivery_date}T12:00:00`).toLocaleDateString("de-DE", {
                  weekday: "long",
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </p>
              {canSend ? (
                <Button
                  type="button"
                  variant="secondary"
                  loading={sendingDate === order.delivery_date}
                  onClick={() => void sendOrder(order.delivery_date)}
                >
                  Jetzt per E-Mail senden
                </Button>
              ) : null}
            </div>
            <ul className="mt-2 space-y-1 text-label-secondary">
              {order.lines.map((line) => (
                <li key={line.id}>
                  {line.quantity}× {line.article_name}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}
